/** ONE CONTINUITY — all area sections. Reference layer: links open source records. */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, ExternalLink, CheckCircle2, Pencil, Trash2 } from "lucide-react";
import {
  PersonDialog, ResponsibilityDialog, InstructionDialog, InsuranceDialog,
  GrantDialog, SuccessionEditor, InfoIndexDialog,
} from "./OneContinuityDialogs";

const AREA_BADGE = {
  COMPLETE: "bg-emerald-100 text-emerald-800",
  ACTION_REQUIRED: "bg-rose-100 text-rose-800",
  REVIEW_REQUIRED: "bg-amber-100 text-amber-800",
};
const CHECK_BADGE = {
  AVAILABLE: "bg-emerald-100 text-emerald-800",
  MISSING: "bg-rose-100 text-rose-800",
  REVIEW_REQUIRED: "bg-amber-100 text-amber-800",
  OUTDATED: "bg-orange-100 text-orange-800",
};
const Badge = ({ v, map = AREA_BADGE }) => (
  <span className={"text-[9px] font-semibold px-1.5 py-0.5 rounded " + (map[v] || "bg-slate-100 text-slate-500")}>
    {(v || "").replace(/_/g, " ")}</span>);
const SCOPE_BADGE = { BUSINESS: "bg-blue-100 text-blue-900", PERSONAL: "bg-purple-100 text-purple-900" };
const Card = ({ title, children, action }) => (
  <div className="border border-slate-200 rounded-xl bg-white p-4">
    <div className="flex items-center mb-3">
      <div className="text-[11px] font-heading font-semibold flex-1">{title}</div>{action}
    </div>{children}
  </div>);
const btnCls = "text-[10.5px] px-2 py-1.5 rounded-md border border-slate-300 inline-flex items-center gap-1";

// ------------------------------------------------------------------ OVERVIEW
export function Overview({ refreshKey }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/continuity/overview").then(({ data }) => setD(data)); }, [refreshKey]);
  if (!d) return null;
  return (
    <div className="space-y-4" data-testid="cont-overview">
      <div className="grid md:grid-cols-2 gap-3">
        {Object.entries(d.readiness).map(([scope, r]) => (
          <div key={scope} className="border border-slate-200 rounded-xl bg-white p-4" data-testid={`cont-readiness-${scope}`}>
            <div className="text-[10px] uppercase tracking-wide text-slate-400">{scope} readiness</div>
            <div className="text-2xl font-heading font-semibold mt-0.5">
              {r.complete} of {r.total} <span className="text-sm text-slate-400 font-normal">areas complete</span></div>
            {r.missing.length > 0 && (
              <div className="mt-1.5 text-[10.5px] text-rose-700">
                Missing: {r.missing.join(" · ")}</div>)}
          </div>))}
        <div className="border border-slate-200 rounded-xl bg-white p-4">
          <div className="text-[10px] uppercase tracking-wide text-slate-400">Expiring / Overdue</div>
          <div className="text-2xl font-heading font-semibold mt-0.5">{d.expiring_30}
            <span className="text-sm text-slate-400 font-normal"> in 30 days</span></div>
          <div className="text-[10.5px] text-rose-700 mt-1">{d.overdue} overdue renewal(s)</div>
        </div>
      </div>
      {Object.entries(d.areas).map(([scope, areas]) => (
        <Card key={scope} title={scope}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5" data-testid={`cont-areas-${scope}`}>
            {Object.entries(areas).map(([name, st]) => (
              <div key={name} className="flex items-center justify-between text-[11.5px] border-b border-slate-50 pb-1">
                <span>{name}</span><Badge v={st} /></div>))}
          </div>
        </Card>))}
    </div>
  );
}

// ---------------------------------------------------------- BUSINESS AREA
export function BusinessArea({ access, refreshKey, bump }) {
  const [pulse, setPulse] = useState([]);
  const [idx, setIdx] = useState([]);
  const [idxEdit, setIdxEdit] = useState(null);
  useEffect(() => {
    api.get("/continuity/business-pulse").then(({ data }) => setPulse(data.items || []));
    api.get("/continuity/info-index").then(({ data }) =>
      setIdx((data.items || []).filter((i) => i.scope === "BUSINESS")));
  }, [refreshKey]);
  return (
    <div className="space-y-4" data-testid="cont-business">
      <Card title="Current critical business work">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="cont-pulse">
          {pulse.map((p) => (
            <Link key={p.label} to={p.path}
                className="border border-slate-200 rounded-lg p-2.5 hover:border-emerald-600 transition-colors">
              <div className="text-xl font-mono font-semibold">{p.count}</div>
              <div className="text-[10px] text-slate-500">{p.label}</div>
            </Link>))}
        </div>
      </Card>
      <SuccessionEditor onSaved={bump} />
      <Card title="Important Information Index — where business records live"
          action={access.admin && <button onClick={() => setIdxEdit({})} data-testid="cont-idx-add"
              className={btnCls}><Plus className="h-3 w-3" /> Add pointer</button>}>
        <InfoIndexList items={idx} onEdit={access.admin ? setIdxEdit : null} />
      </Card>
      {idxEdit && <InfoIndexDialog item={idxEdit} onClose={() => setIdxEdit(null)}
          onDone={() => { setIdxEdit(null); bump(); }} />}
    </div>
  );
}

export function InfoIndexList({ items, onEdit }) {
  return (
    <div className="space-y-1.5" data-testid="cont-info-index">
      {items.length === 0 && <div className="text-[11px] text-slate-400">No pointers yet.</div>}
      {items.map((i) => (
        <div key={i.id} className="border border-slate-200 rounded-lg px-3 py-2 flex items-start gap-2">
          <div className="flex-1">
            <div className="text-[11.5px] font-medium">{i.label}
              <span className="text-slate-400 font-normal"> → {i.module_label}</span></div>
            <div className="text-[10px] text-slate-500 mt-0.5">
              {i.what_is_this && <span><b>What:</b> {i.what_is_this} · </span>}
              {i.why_it_matters && <span><b>Why:</b> {i.why_it_matters} · </span>}
              {i.who_handles && <span><b>Handled by:</b> {i.who_handles}</span>}
            </div>
          </div>
          {onEdit && <button onClick={() => onEdit(i)} className="p-1 text-slate-400 hover:text-slate-700"
              data-testid={`cont-idx-edit-${i.id}`}><Pencil className="h-3 w-3" /></button>}
          <Link to={i.path} data-testid={`cont-idx-open-${i.id}`}
              className="text-[10px] px-2 py-1 rounded bg-slate-900 text-white inline-flex items-center gap-1">
            <ExternalLink className="h-3 w-3" /> Open record</Link>
        </div>))}
    </div>
  );
}

// ------------------------------------------------------ PERSONAL / FAMILY
export function PersonalArea({ goto }) {
  const tiles = [
    ["Money & Banking", "Bank accounts and monthly statements", "/admin/finance-vault", null],
    ["Tax Documents", "ITR, computation and tax records", "/admin/finance-vault", null],
    ["Insurance", "Policies, renewal dates and nominees", null, "INSURANCE"],
    ["Important Documents", "What exists, what is missing", null, "IMPORTANT DOCUMENTS"],
    ["Advisers & Important People", "Who to call and why they matter", null, "KEY PEOPLE"],
    ["What Needs Attention", "Everything that needs action now", null, "MISSING INFORMATION"],
    ["Important Instructions", "Step-by-step guidance if someone is unavailable", null, "IMPORTANT INSTRUCTIONS"],
    ["Renewals", "What is expiring and when", null, "RENEWALS"],
  ];
  return (
    <div className="space-y-3" data-testid="cont-personal">
      <div className="text-[12px] text-slate-600 max-w-xl">
        This area is written in plain language for family members — no internal jargon.
        Everything below opens the actual record or list.</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {tiles.map(([title, desc, href, sec]) => href ? (
          <Link key={title} to={href} data-testid={`cont-family-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="border border-slate-200 rounded-xl bg-white p-4 hover:border-emerald-600 transition-colors">
            <div className="text-[12px] font-semibold">{title}</div>
            <div className="text-[10.5px] text-slate-500 mt-1">{desc}</div>
          </Link>
        ) : (
          <button key={title} onClick={() => goto(sec)}
              data-testid={`cont-family-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="border border-slate-200 rounded-xl bg-white p-4 text-left hover:border-emerald-600 transition-colors">
            <div className="text-[12px] font-semibold">{title}</div>
            <div className="text-[10.5px] text-slate-500 mt-1">{desc}</div>
          </button>))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------- CHECKLIST
export function ChecklistSection({ access, refreshKey, bump, goto }) {
  const [scope, setScope] = useState(access.business ? "BUSINESS" : "PERSONAL");
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/continuity/checklist", { params: { scope } })
      .then(({ data }) => setItems(data.items || []));
  }, [scope, refreshKey]);
  const override = async (item) => {
    const st = window.prompt(`Status for "${item.label}" (AVAILABLE / MISSING / REVIEW_REQUIRED / OUTDATED):`,
      item.status);
    if (!st) return;
    try {
      await api.put("/continuity/checklist/manual",
        { key: item.key, scope, status: st.trim().toUpperCase() });
      toast.success("Checklist updated"); bump();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const SOURCE_PATH = { FINANCE_VAULT: "/admin/finance-vault", KYC_VAULT: "/admin/corporate-kyc",
    BANK_ACCOUNTS: "/admin/finance-vault", CONTRACTS: "/commercial/contracts" };
  return (
    <div className="space-y-3" data-testid="cont-checklist">
      <div className="flex items-center gap-2">
        <div className="text-[12px] font-semibold flex-1">Important document checklist</div>
        {access.business && access.personal && (
          <select value={scope} onChange={(e) => setScope(e.target.value)} data-testid="cont-check-scope"
              className="text-[11px] border border-slate-300 rounded-md px-2 py-1.5">
            <option value="BUSINESS">BUSINESS</option><option value="PERSONAL">PERSONAL</option>
          </select>)}
      </div>
      <div className="border border-slate-200 rounded-lg bg-white divide-y divide-slate-100">
        {items.map((i) => (
          <div key={i.key} className="flex items-center gap-2 px-3 py-2" data-testid={`cont-check-${i.key}`}>
            <span className="text-[11.5px] flex-1">{i.label}
              <span className="text-[9px] text-slate-400 ml-1">{i.source.replace(/_/g, " ")}</span></span>
            <Badge v={i.status} map={CHECK_BADGE} />
            {SOURCE_PATH[i.source] && (
              <Link to={SOURCE_PATH[i.source]} className="text-[9.5px] text-emerald-800 underline">open</Link>)}
            {i.source === "INSURANCE" && (
              <button onClick={() => goto("INSURANCE")} data-testid={`cont-check-open-${i.key}`}
                  className="text-[9.5px] text-emerald-800 underline">open</button>)}
            {i.source === "MANUAL" && (
              <button onClick={() => override(i)} data-testid={`cont-check-set-${i.key}`}
                  className="text-[9.5px] px-1.5 py-0.5 rounded border border-slate-300">set</button>)}
          </div>))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------- KEY PEOPLE
export function KeyPeople({ access, refreshKey, bump }) {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  useEffect(() => {
    api.get("/continuity/people").then(({ data }) => setItems(data.items || []));
  }, [refreshKey]);
  const confirm = async (p) => {
    await api.post(`/continuity/people/${p.id}/confirm`);
    toast.success(`${p.name} confirmed`); bump();
  };
  const del = async (p) => {
    if (!window.confirm(`Remove ${p.name}?`)) return;
    try { await api.delete(`/continuity/people/${p.id}`); bump(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div className="space-y-3" data-testid="cont-people">
      <div className="flex items-center gap-2">
        <div className="text-[12px] font-semibold flex-1">Key people & advisers</div>
        <button onClick={() => setEdit({})} data-testid="cont-person-add" className={btnCls}>
          <Plus className="h-3 w-3" /> Add key person</button>
      </div>
      <div className="grid md:grid-cols-2 gap-2">
        {items.length === 0 && <div className="text-[11px] text-slate-400">Nobody recorded yet — start with your CA.</div>}
        {items.map((p) => (
          <div key={p.id} className="border border-slate-200 rounded-xl bg-white p-3" data-testid={`cont-person-${p.id}`}>
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <div className="text-[12px] font-semibold">{p.name}
                  <span className={"ml-1.5 text-[8.5px] px-1.5 py-0.5 rounded " + (SCOPE_BADGE[p.scope] || "")}>{p.scope}</span>
                  {p.confirmed && <CheckCircle2 className="inline h-3 w-3 text-emerald-600 ml-1" />}</div>
                <div className="text-[10px] text-slate-500">{(p.category || "").replace(/_/g, " ")}
                  {p.organisation ? ` · ${p.organisation}` : ""}</div>
              </div>
              <button onClick={() => setEdit(p)} className="p-1 text-slate-400 hover:text-slate-700"
                  data-testid={`cont-person-edit-${p.id}`}><Pencil className="h-3 w-3" /></button>
              <button onClick={() => del(p)} className="p-1 text-slate-400 hover:text-rose-600">
                <Trash2 className="h-3 w-3" /></button>
            </div>
            <div className="text-[10.5px] mt-1.5 space-y-0.5">
              {p.phone && <div>📞 {p.phone}</div>}
              {p.email && <div>✉️ {p.email}</div>}
              {p.why_important && <div className="text-slate-500"><b>Why:</b> {p.why_important}</div>}
              {p.handles && <div className="text-slate-500"><b>Handles:</b> {p.handles}</div>}
            </div>
            {!p.confirmed && (
              <button onClick={() => confirm(p)} data-testid={`cont-person-confirm-${p.id}`}
                  className="mt-2 text-[10px] px-2 py-1 rounded border border-emerald-400 text-emerald-700">
                Confirm contact</button>)}
          </div>))}
      </div>
      {edit && <PersonDialog person={edit} access={access} onClose={() => setEdit(null)}
          onDone={() => { setEdit(null); bump(); }} />}
    </div>
  );
}

// ------------------------------------------------------ RESPONSIBILITY MAP
export function ResponsibilityMap({ refreshKey, bump }) {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  useEffect(() => {
    api.get("/continuity/responsibilities", { params: { scope: "BUSINESS" } })
      .then(({ data }) => setItems(data.items || []));
  }, [refreshKey]);
  const del = async (r) => {
    if (!window.confirm(`Remove ${r.function}?`)) return;
    try { await api.delete(`/continuity/responsibilities/${r.id}`); bump(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div className="space-y-3" data-testid="cont-responsibilities">
      <div className="flex items-center gap-2">
        <div className="text-[12px] font-semibold flex-1">Business responsibility map</div>
        <button onClick={() => setEdit({})} data-testid="cont-resp-add" className={btnCls}>
          <Plus className="h-3 w-3" /> Add responsibility</button>
      </div>
      <div className="border border-slate-200 rounded-lg bg-white overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead className="bg-slate-50 text-slate-500"><tr>
            <th className="text-left px-3 py-2">Function</th><th className="text-left px-3 py-2">Primary</th>
            <th className="text-left px-3 py-2">Backup</th><th className="text-left px-3 py-2">Escalation</th>
            <th className="text-left px-3 py-2">Module / Instructions</th><th className="w-16"></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 px-3 py-8">
              No responsibilities mapped yet.</td></tr>}
            {items.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`cont-resp-${r.id}`}>
                <td className="px-3 py-2 font-medium">{r.function}</td>
                <td className="px-3 py-2">{r.primary || "—"}</td>
                <td className="px-3 py-2">{r.backup || "—"}</td>
                <td className="px-3 py-2">{r.escalation || "—"}</td>
                <td className="px-3 py-2 text-[10px] text-slate-500">
                  {r.related_module && <Link to={r.related_module} className="text-emerald-800 underline mr-1">module</Link>}
                  {r.instructions}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button onClick={() => setEdit(r)} className="p-1 text-slate-400 hover:text-slate-700"
                      data-testid={`cont-resp-edit-${r.id}`}><Pencil className="h-3 w-3" /></button>
                  <button onClick={() => del(r)} className="p-1 text-slate-400 hover:text-rose-600">
                    <Trash2 className="h-3 w-3" /></button></td>
              </tr>))}
          </tbody>
        </table>
      </div>
      {edit && <ResponsibilityDialog item={edit} onClose={() => setEdit(null)}
          onDone={() => { setEdit(null); bump(); }} />}
    </div>
  );
}

// ---------------------------------------------------------------- RENEWALS
export function Renewals({ refreshKey }) {
  const [win, setWin] = useState("30");
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/continuity/renewals", { params: { window: win } })
      .then(({ data }) => setItems(data.items || []));
  }, [win, refreshKey]);
  return (
    <div className="space-y-3" data-testid="cont-renewals">
      <div className="flex items-center gap-1.5">
        <div className="text-[12px] font-semibold flex-1">Renewal centre</div>
        {["30", "60", "90", "overdue"].map((w) => (
          <button key={w} onClick={() => setWin(w)} data-testid={`cont-ren-${w}`}
              className={"text-[10px] px-2.5 py-1.5 rounded-full border " +
                (win === w ? "bg-emerald-950 text-white border-emerald-950" : "border-slate-300")}>
            {w === "overdue" ? "OVERDUE" : `NEXT ${w} DAYS`}</button>))}
      </div>
      <div className="space-y-1.5">
        {items.length === 0 && <div className="text-[11.5px] text-slate-400 border border-dashed border-slate-300 rounded-lg p-6 text-center">
          Nothing in this window.</div>}
        {items.map((r, i) => (
          <div key={i} className={"flex items-center gap-2 border rounded-lg px-3 py-2 " +
              (r.days < 0 ? "border-rose-300 bg-rose-50/60" : "border-slate-200 bg-white")}
              data-testid={`cont-renewal-${i}`}>
            <span className="text-[8.5px] font-semibold px-1.5 py-0.5 rounded bg-slate-900 text-white">{r.source}</span>
            <span className="text-[11.5px] flex-1">{r.label}</span>
            <span className="font-mono text-[10.5px]">{r.date}</span>
            <span className={"font-mono text-[10.5px] font-semibold " + (r.days < 0 ? "text-rose-700" : "text-slate-600")}>
              {r.days < 0 ? `${-r.days}d overdue` : `${r.days}d`}</span>
            {!r.path.startsWith("internal:") && (
              <Link to={r.path} data-testid={`cont-renewal-open-${i}`}
                  className="text-[10px] px-2 py-1 rounded bg-slate-900 text-white">Open source</Link>)}
          </div>))}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------- FINANCE
export function FinanceArea() {
  const [fv, setFv] = useState(null);
  useEffect(() => {
    api.get("/finance-vault/overview").then(({ data }) => setFv(data)).catch(() => setFv(null));
  }, []);
  const links = [
    ["Finance Vault", "ITR, Balance Sheets, P&L, bank statements, CA documents, packs"],
    ["Corporate ITR & tax records", "One click into the ITR / Income Tax section"],
    ["Monthly bank statements", "Every account's APR–MAR statement matrix"],
    ["Financial packs", "Organised ZIPs for a full financial year"],
  ];
  return (
    <div className="space-y-3" data-testid="cont-finance">
      <div className="text-[12px] text-slate-600 max-w-xl">
        Continuity never duplicates financial documents — everything lives in the Finance Vault.</div>
      {fv && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {fv.corporate && [["ITR", fv.corporate.itr], ["Balance Sheet", fv.corporate.balance_sheet],
            ["P&L", fv.corporate.pnl],
            ["Bank statements", `${fv.corporate.bank_statements.available}/${fv.corporate.bank_statements.expected}`]]
            .map(([l, v]) => (
              <div key={l} className="border border-slate-200 rounded-lg bg-white p-2.5">
                <div className="text-[9px] uppercase text-slate-400">{l}</div>
                <div className="text-[12px] font-semibold">{typeof v === "string" && v.includes("/")
                  ? v : <Badge v={v === "AVAILABLE" ? "COMPLETE" : "ACTION_REQUIRED"} />}</div>
              </div>))}
        </div>)}
      <div className="grid sm:grid-cols-2 gap-3">
        {links.map(([t, d]) => (
          <Link key={t} to="/admin/finance-vault" data-testid={`cont-fin-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="border border-slate-200 rounded-xl bg-white p-4 hover:border-emerald-600 transition-colors">
            <div className="text-[12px] font-semibold flex items-center gap-1">{t}
              <ExternalLink className="h-3 w-3 text-slate-400" /></div>
            <div className="text-[10.5px] text-slate-500 mt-1">{d}</div>
          </Link>))}
      </div>
    </div>
  );
}

// --------------------------------------------------------------- INSURANCE
export function InsuranceSection({ access, refreshKey, bump }) {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  useEffect(() => {
    api.get("/continuity/insurance").then(({ data }) => setItems(data.items || []));
  }, [refreshKey]);
  const del = async (i) => {
    if (!window.confirm(`Remove policy ${i.policy_number}?`)) return;
    try { await api.delete(`/continuity/insurance/${i.id}`); bump(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const NOM = { CONFIRMED: "bg-emerald-100 text-emerald-800", NOT_CONFIRMED: "bg-rose-100 text-rose-800",
    REVIEW_REQUIRED: "bg-amber-100 text-amber-800", NOT_APPLICABLE: "bg-slate-100 text-slate-500" };
  return (
    <div className="space-y-3" data-testid="cont-insurance">
      <div className="flex items-center gap-2">
        <div className="text-[12px] font-semibold flex-1">Insurance register</div>
        <button onClick={() => setEdit({})} data-testid="cont-ins-add" className={btnCls}>
          <Plus className="h-3 w-3" /> Add policy</button>
      </div>
      <div className="border border-slate-200 rounded-lg bg-white overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead className="bg-slate-50 text-slate-500"><tr>
            <th className="text-left px-3 py-2">Policy</th><th className="text-left px-3 py-2">Scope</th>
            <th className="text-left px-3 py-2">Holder</th><th className="text-left px-3 py-2">Coverage</th>
            <th className="text-left px-3 py-2">Valid</th><th className="text-right px-3 py-2">Premium</th>
            <th className="text-left px-3 py-2">Nomination</th><th className="w-16"></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 px-3 py-8">
              No policies in the register yet.</td></tr>}
            {items.map((i) => (
              <tr key={i.id} className="border-t border-slate-100" data-testid={`cont-ins-${i.id}`}>
                <td className="px-3 py-2"><span className="font-medium">{i.insurer}</span>
                  <div className="text-[9.5px] text-slate-400 font-mono">{i.policy_type} · {i.policy_number}</div></td>
                <td className="px-3 py-2"><span className={"text-[8.5px] font-semibold px-1.5 py-0.5 rounded " +
                    (SCOPE_BADGE[i.scope] || "")}>{i.scope}</span></td>
                <td className="px-3 py-2">{i.policy_holder}</td>
                <td className="px-3 py-2 text-[10px] text-slate-500 max-w-[180px]">{i.coverage_summary || "—"}</td>
                <td className="px-3 py-2 font-mono text-[10px]">{i.start_date || "?"} → {i.end_date || "?"}</td>
                <td className="px-3 py-2 text-right font-mono">{i.premium ? i.premium.toLocaleString("en-IN") : "—"}</td>
                <td className="px-3 py-2"><Badge v={i.nomination_status} map={NOM} />
                  {i.nominee && <div className="text-[9px] text-slate-400">{i.nominee}</div>}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button onClick={() => setEdit(i)} className="p-1 text-slate-400 hover:text-slate-700"
                      data-testid={`cont-ins-edit-${i.id}`}><Pencil className="h-3 w-3" /></button>
                  <button onClick={() => del(i)} className="p-1 text-slate-400 hover:text-rose-600">
                    <Trash2 className="h-3 w-3" /></button></td>
              </tr>))}
          </tbody>
        </table>
      </div>
      {edit && <InsuranceDialog item={edit} access={access} onClose={() => setEdit(null)}
          onDone={() => { setEdit(null); bump(); }} />}
    </div>
  );
}

// ------------------------------------------------------------------ ASSETS
export function AssetsArea() {
  const links = [
    ["All assets", "/assets", "Full asset register across customers and sites"],
    ["Stock & warehouses", "/operations/stock-inventory", "Suntronix stock, GRNs, serials, transfers"],
    ["Customer-owned stock", "/operations/stock-inventory", "Assets held for customers in our warehouses"],
    ["Employee fulfilments", "/operations/employee-fulfilment", "Devices being prepared for joiners"],
  ];
  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="cont-assets">
      {links.map(([t, path, d]) => (
        <Link key={t} to={path} className="border border-slate-200 rounded-xl bg-white p-4 hover:border-emerald-600 transition-colors"
            data-testid={`cont-asset-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          <div className="text-[12px] font-semibold flex items-center gap-1">{t}
            <ExternalLink className="h-3 w-3 text-slate-400" /></div>
          <div className="text-[10.5px] text-slate-500 mt-1">{d}</div>
        </Link>))}
    </div>
  );
}

// ---------------------------------------------------------------- PLAYBOOK
export function Playbook({ access, refreshKey, bump }) {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const PH = ["IMMEDIATE", "FIRST_24_HOURS", "FIRST_7_DAYS", "FIRST_30_DAYS", "ONGOING"];
  useEffect(() => {
    api.get("/continuity/playbook").then(({ data }) => setItems(data.items || []));
  }, [refreshKey]);
  const del = async (p) => {
    if (!window.confirm("Remove this instruction?")) return;
    try { await api.delete(`/continuity/playbook/${p.id}`); bump(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div className="space-y-4" data-testid="cont-playbook">
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <div className="text-[12px] font-semibold">Continuity playbook — if a key person is unavailable</div>
          <div className="text-[10px] text-slate-400">Ordered instructions with responsible people and links.</div>
        </div>
        <button onClick={() => setEdit({})} data-testid="cont-play-add" className={btnCls}>
          <Plus className="h-3 w-3" /> Add instruction</button>
      </div>
      {PH.map((ph) => {
        const rows = items.filter((i) => i.phase === ph);
        if (!rows.length) return null;
        return (
          <Card key={ph} title={ph.replace(/_/g, " ")}>
            <div className="space-y-1.5">
              {rows.sort((a, b) => (a.order || 0) - (b.order || 0)).map((p) => (
                <div key={p.id} className="flex items-start gap-2 border border-slate-100 rounded-lg px-3 py-2"
                    data-testid={`cont-play-${p.id}`}>
                  <div className="flex-1">
                    <div className="text-[11.5px] font-medium">{p.action}
                      {p.priority && <span className="ml-1.5 text-[8.5px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-800">{p.priority}</span>}
                      <span className={"ml-1.5 text-[8.5px] px-1.5 py-0.5 rounded " + (SCOPE_BADGE[p.scope] || "")}>{p.scope}</span></div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      {p.responsible && <span>→ {p.responsible}</span>}
                      {p.backup && <span className="text-slate-400"> (backup: {p.backup})</span>}
                      {p.notes && <span> · {p.notes}</span>}</div>
                  </div>
                  {p.related_module && <Link to={p.related_module}
                      className="text-[10px] px-2 py-1 rounded bg-slate-900 text-white">Open</Link>}
                  <button onClick={() => setEdit(p)} className="p-1 text-slate-400 hover:text-slate-700"
                      data-testid={`cont-play-edit-${p.id}`}><Pencil className="h-3 w-3" /></button>
                  <button onClick={() => del(p)} className="p-1 text-slate-400 hover:text-rose-600">
                    <Trash2 className="h-3 w-3" /></button>
                </div>))}
            </div>
          </Card>);
      })}
      {items.length === 0 && <div className="text-[11.5px] text-slate-400 border border-dashed border-slate-300 rounded-lg p-8 text-center">
        No instructions yet — add the first steps someone should take.</div>}
      {edit && <InstructionDialog item={edit} access={access} onClose={() => setEdit(null)}
          onDone={() => { setEdit(null); bump(); }} />}
    </div>
  );
}

// ----------------------------------------------------------------- MISSING
export function MissingSection({ refreshKey }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/continuity/actions").then(({ data }) => setItems(data.items || []));
  }, [refreshKey]);
  return (
    <div className="space-y-3" data-testid="cont-missing">
      <div className="text-[12px] font-semibold">What needs attention</div>
      {items.length === 0 && <div className="text-[11.5px] text-emerald-700 border border-emerald-200 bg-emerald-50 rounded-lg px-3 py-4">
        Nothing outstanding — continuity information is complete.</div>}
      <div className="space-y-1.5">
        {items.map((m, i) => (
          <div key={i} className="flex items-center gap-2 border border-amber-200 bg-amber-50/50 rounded-lg px-3 py-2"
              data-testid={`cont-action-${i}`}>
            <span className="text-[8.5px] font-semibold px-1.5 py-0.5 rounded bg-amber-200 text-amber-900">
              {m.kind.replace(/_/g, " ")}</span>
            <span className="text-[11.5px] flex-1">{m.label}</span>
          </div>))}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------- HISTORY
export function HistorySection({ refreshKey }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/continuity/audit").then(({ data }) => setItems(data.items || []));
  }, [refreshKey]);
  return (
    <div className="space-y-3" data-testid="cont-history">
      <div className="text-[12px] font-semibold">History — audited continuity activity</div>
      <div className="border border-slate-200 rounded-lg bg-white overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead className="bg-slate-50 text-slate-500"><tr>
            <th className="text-left px-3 py-2">When</th><th className="text-left px-3 py-2">Action</th>
            <th className="text-left px-3 py-2">Scope</th><th className="text-left px-3 py-2">Detail</th>
            <th className="text-left px-3 py-2">By</th></tr></thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id} className="border-t border-slate-100">
                <td className="px-3 py-1.5 font-mono text-[10px]">{(e.at || "").replace("T", " ").slice(0, 16)}</td>
                <td className="px-3 py-1.5 text-[10px] font-semibold">{e.action.replace(/_/g, " ")}</td>
                <td className="px-3 py-1.5 text-[10px]">{e.scope || "—"}</td>
                <td className="px-3 py-1.5 text-[10px]">{e.detail || "—"}</td>
                <td className="px-3 py-1.5 text-[10px] text-slate-500">{e.actor}</td>
              </tr>))}
            {items.length === 0 && <tr><td colSpan={5} className="text-center text-slate-400 px-3 py-8">No activity yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// --------------------------------------------------------- ACCESS & SETTINGS
export function AccessSettings({ refreshKey, bump }) {
  const [grants, setGrants] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [grantOpen, setGrantOpen] = useState(false);
  useEffect(() => {
    api.get("/continuity/grants").then(({ data }) => setGrants(data.items || []));
    api.get("/continuity/config").then(({ data }) => setCfg(data.config));
  }, [refreshKey]);
  const revoke = async (g) => {
    if (!window.confirm(`Revoke continuity access for ${g.email}?`)) return;
    try { await api.delete(`/continuity/grants/${g.id}`); bump(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const saveList = async (key, text) => {
    try {
      await api.put("/continuity/config", { [key]: text.split("\n").map((s) => s.trim()).filter(Boolean) });
      toast.success("Saved"); bump();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-4 max-w-2xl" data-testid="cont-settings">
      <Card title="Continuity access grants (explicit only — never inferred from role)"
          action={<button onClick={() => setGrantOpen(true)} data-testid="cont-grant-add" className={btnCls}>
            <Plus className="h-3 w-3" /> Grant access</button>}>
        <div className="space-y-1.5">
          {grants.map((g) => (
            <div key={g.id} className="flex items-center gap-2 text-[11px] border border-slate-100 rounded-lg px-3 py-2"
                data-testid={`cont-grant-${g.id}`}>
              <span className="flex-1 font-medium">{g.email}</span>
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-900 font-semibold">
                {g.profile.replace(/_/g, " ")}</span>
              {g.scopes.map((s) => <span key={s} className={"text-[8.5px] px-1.5 py-0.5 rounded " + (SCOPE_BADGE[s] || "")}>{s}</span>)}
              <button onClick={() => revoke(g)} data-testid={`cont-grant-revoke-${g.id}`}
                  className="p-1 text-slate-400 hover:text-rose-600"><Trash2 className="h-3 w-3" /></button>
            </div>))}
        </div>
      </Card>
      {cfg && <>
        <TextListCard title="Responsibility functions" testid="cont-cfg-functions"
            initial={cfg.functions} onSave={(t) => saveList("functions", t)} />
        <TextListCard title="Adviser categories" testid="cont-cfg-advisers"
            initial={cfg.adviser_categories} onSave={(t) => saveList("adviser_categories", t)} />
      </>}
      {grantOpen && <GrantDialog onClose={() => setGrantOpen(false)}
          onDone={() => { setGrantOpen(false); bump(); }} />}
    </div>
  );
}

function TextListCard({ title, initial, onSave, testid }) {
  const [text, setText] = useState((initial || []).join("\n"));
  return (
    <Card title={title} action={
      <button onClick={() => onSave(text)} data-testid={`${testid}-save`}
          className="text-[10px] px-2.5 py-1 rounded bg-emerald-950 text-white">Save</button>}>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} data-testid={testid}
          className="w-full text-[11px] font-mono border border-slate-200 rounded-md px-2 py-1.5" />
    </Card>
  );
}
